import {
    AnyAction,
    createSlice,
    PayloadAction,
    Reducer,
} from '@reduxjs/toolkit';

import {
    OtherIncomeApiPayload,
    OtherIncomeDeletePayload,
    OtherIncomeDetailUpsertPayload,
    OtherIncomeEdit,
    OtherIncomeEndpoints,
    OtherIncomeState,
} from './otherIncomeTypes';
import { initialOtherIncomeState as initialState } from './otherIncomeStateDefaultValue';
import { ApiStatus } from '../../types';

const otherIncomeSlice = createSlice({
    name: 'otherIncome',
    initialState,
    reducers: {
        getOtherIncome(state) {
            state.incomeApiStatus = ApiStatus.Pending;
        },
        getOtherIncomeFulfilled(state, action: PayloadAction<OtherIncomeApiPayload>) {
            const { content, incomeDetails } = action.payload;

            state.content = JSON.parse(content);
            state.incomeDetails = incomeDetails;
            state.incomeApiStatus = ApiStatus.Fulfilled;
        },
        getOtherIncomeRejected(state) {
            state.incomeApiStatus = ApiStatus.Rejected;
        },
        
        setOtherIncomeAddMode: (state) => {
            state.isAddMode = true;
        },

        setOtherIncomeEditMode(state, action: PayloadAction<number>) {
            state.incomeDetails[action.payload] = {
                ...state.incomeDetails[action.payload],
                recordNumber: action.payload,
                isEditMode: true,
            };
        },

        cancelOtherIncomeAddMode: (state) => {
            state.isAddMode = false;
            state.lastVisitedItem = {
                id: null,
                mode: 'add',
                isSaved: false,
            };
        },

        cancelOtherIncomeEditMode: (state, action: PayloadAction<number>) => {
            const recordNumber = action.payload;

            state.incomeDetails[recordNumber].isEditMode = false;
            state.lastVisitedItem = {
                id: recordNumber,
                mode: 'edit',
                isSaved: false,
            };
        },

        addOtherIncome: (state, action: PayloadAction<OtherIncomeDetailUpsertPayload>) => state,

        addOtherIncomeFulfilled: (state, action: PayloadAction<OtherIncomeEdit>) => {
            const lastIndex = Object.keys(state.incomeDetails).length;

            state.incomeDetails[lastIndex] = action.payload;
            state.isAddMode = false;
            state.lastVisitedItem = {
                id: lastIndex,
                mode: 'add',
                isSaved: true,
            };
        },

        addOtherIncomeRejected: (state) => state,

        updateOtherIncome: (state, action: PayloadAction<OtherIncomeDetailUpsertPayload>) => state,

        updateOtherIncomeFulfilled: (state, action: PayloadAction<OtherIncomeEdit>) => {
            const { recordNumber } = action.payload;

            if (recordNumber === undefined) return;

            state.incomeDetails[recordNumber] = action.payload;
            state.incomeDetails[recordNumber].isEditMode = false;
            state.lastVisitedItem = {
                id: recordNumber,
                mode: 'edit',
                isSaved: true,
            };
        },

        updateOtherIncomeRejected: (state) => state,

        deleteOtherIncome: (state, action: PayloadAction<OtherIncomeDeletePayload>) => state,
        deleteOtherIncomeFulfilled(state, action: PayloadAction<number>) {
            const incomeId = action.payload;

            state.incomeDetails = Object.entries(state.incomeDetails)
                .filter(([_, incomeDetail]) => incomeDetail.incomeId !== incomeId)
                .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        },
        deleteOtherIncomeRejected: (state) => state,
        updateOtherIncomeEndPoints: (state, action: PayloadAction<OtherIncomeEndpoints>) => {
            state.apiEndpoints = action.payload;
        },
        submitOtherIncome: (state) => state,
        resetOtherIncomeStatus(state) {
            state.apiStatus = ApiStatus.Idle;

            state.incomeApiStatus = ApiStatus.Idle;
            state.isAddMode = false;
        },
        resetOtherIncomeLastVisited: (state) => {
            state.lastVisitedItem = {
                id: null,
                mode: null,
                isSaved: false,
            };
        },
    },
});

export const {
    setOtherIncomeAddMode,
    setOtherIncomeEditMode,
    cancelOtherIncomeAddMode,
    cancelOtherIncomeEditMode,
    getOtherIncome,
    getOtherIncomeFulfilled,
    getOtherIncomeRejected,
    addOtherIncome,
    addOtherIncomeFulfilled,
    addOtherIncomeRejected,
    updateOtherIncome,
    updateOtherIncomeFulfilled,
    updateOtherIncomeRejected,
    deleteOtherIncome,
    deleteOtherIncomeFulfilled,
    deleteOtherIncomeRejected,
    updateOtherIncomeEndPoints,
    submitOtherIncome,
    resetOtherIncomeStatus,
    resetOtherIncomeLastVisited,
} = otherIncomeSlice.actions;

export const otherIncomeReducer: Reducer<OtherIncomeState, AnyAction> =
    otherIncomeSlice.reducer;
